import { useState, useEffect, useCallback } from "react";
import { Tag, Star, Plus, Trash2, Edit2, Check, X, Clock, Send, Loader2, Eye, EyeOff, MessageCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function OffersReviewsPage() {
  const { token } = useAuth();
  const [tab, setTab] = useState("offers");
  const [offers, setOffers] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [sendingLink, setSendingLink] = useState(null);
  const [linkPhone, setLinkPhone] = useState("");
  const [linkName, setLinkName] = useState("");

  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const fetchAll = useCallback(async () => {
    try {
      const [oRes, rRes] = await Promise.all([
        fetch(`${API_BASE}/api/offers`, { headers }),
        fetch(`${API_BASE}/api/reviews`, { headers })
      ]);
      if (oRes.ok) setOffers(await oRes.json());
      if (rRes.ok) setReviews(await rRes.json());
    } catch (e) { console.error(e); }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const formatDate = (d) => {
    if (!d) return "";
    try { return new Date(d).toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric" }); }
    catch { return d; }
  };

  // ===== OFFERS =====
  const saveOffer = async () => {
    setSaving(true);
    try {
      const url = editItem ? `${API_BASE}/api/offers/${editItem.id}` : `${API_BASE}/api/offers`;
      const method = editItem ? "PUT" : "POST";
      const res = await fetch(url, { method, headers, body: JSON.stringify(form) });
      if (res.ok) { setShowForm(false); setEditItem(null); setForm({}); fetchAll(); }
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const deleteOffer = async (id) => {
    await fetch(`${API_BASE}/api/offers/${id}`, { method: "DELETE", headers });
    fetchAll();
  };

  const toggleOfferActive = async (offer) => {
    await fetch(`${API_BASE}/api/offers/${offer.id}`, {
      method: "PUT", headers, body: JSON.stringify({ is_active: !offer.is_active })
    });
    fetchAll();
  };

  // ===== REVIEWS =====
  const toggleApproval = async (review) => {
    await fetch(`${API_BASE}/api/reviews/${review.id}`, {
      method: "PUT", headers, body: JSON.stringify({ approved: !review.approved })
    });
    fetchAll();
  };

  const deleteReview = async (id) => {
    await fetch(`${API_BASE}/api/reviews/${id}`, { method: "DELETE", headers });
    fetchAll();
  };

  const sendReviewLink = async () => {
    if (!linkPhone.trim()) return;
    setSendingLink(true);
    try {
      await fetch(`${API_BASE}/api/reviews/send-link`, {
        method: "POST", headers,
        body: JSON.stringify({ phone: linkPhone, patient_name: linkName })
      });
      setLinkPhone(""); setLinkName(""); setSendingLink(null);
    } catch (e) { console.error(e); }
    finally { setSendingLink(false); }
  };

  const offerForm = () => (
    <div className="bg-amber-50 rounded-2xl border border-amber-200 p-5 mb-6" data-testid="offer-form">
      <h3 className="font-bold text-amber-800 mb-4">{editItem ? "تعديل العرض" : "إضافة عرض جديد"}</h3>
      <div className="grid sm:grid-cols-2 gap-3">
        <input value={form.title_ar || ""} onChange={e => setForm(p => ({ ...p, title_ar: e.target.value }))} placeholder="عنوان العرض (عربي)" className="px-4 py-2.5 rounded-xl border text-sm focus:border-amber-400 focus:outline-none" data-testid="offer-title-ar" />
        <input value={form.title_en || ""} onChange={e => setForm(p => ({ ...p, title_en: e.target.value }))} placeholder="Offer Title (English)" className="px-4 py-2.5 rounded-xl border text-sm focus:border-amber-400 focus:outline-none" data-testid="offer-title-en" />
        <input value={form.description_ar || ""} onChange={e => setForm(p => ({ ...p, description_ar: e.target.value }))} placeholder="وصف العرض (عربي)" className="px-4 py-2.5 rounded-xl border text-sm focus:border-amber-400 focus:outline-none" />
        <input value={form.description_en || ""} onChange={e => setForm(p => ({ ...p, description_en: e.target.value }))} placeholder="Description (English)" className="px-4 py-2.5 rounded-xl border text-sm focus:border-amber-400 focus:outline-none" />
        <input value={form.discount_percent || ""} onChange={e => setForm(p => ({ ...p, discount_percent: Number(e.target.value) || 0 }))} placeholder="نسبة الخصم %" type="number" className="px-4 py-2.5 rounded-xl border text-sm focus:border-amber-400 focus:outline-none" data-testid="offer-discount" />
        <input value={form.original_price || ""} onChange={e => setForm(p => ({ ...p, original_price: Number(e.target.value) || 0 }))} placeholder="السعر الأصلي" type="number" className="px-4 py-2.5 rounded-xl border text-sm focus:border-amber-400 focus:outline-none" />
        <input value={form.offer_price || ""} onChange={e => setForm(p => ({ ...p, offer_price: Number(e.target.value) || 0 }))} placeholder="سعر العرض" type="number" className="px-4 py-2.5 rounded-xl border text-sm focus:border-amber-400 focus:outline-none" data-testid="offer-price" />
        <input value={form.max_clients || ""} onChange={e => setForm(p => ({ ...p, max_clients: Number(e.target.value) || 0 }))} placeholder="الحد الأقصى للعملاء (0 = بلا حد)" type="number" className="px-4 py-2.5 rounded-xl border text-sm focus:border-amber-400 focus:outline-none" />
        <input value={form.end_date || ""} onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))} type="datetime-local" className="px-4 py-2.5 rounded-xl border text-sm focus:border-amber-400 focus:outline-none" data-testid="offer-end-date" />
      </div>
      <div className="flex gap-2 mt-4">
        <button onClick={saveOffer} disabled={saving || !form.title_ar} className="flex items-center gap-2 px-6 py-2.5 bg-amber-500 text-white rounded-xl text-sm font-bold hover:bg-amber-400 disabled:opacity-40 transition-colors" data-testid="offer-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}حفظ
        </button>
        <button onClick={() => { setShowForm(false); setEditItem(null); setForm({}); }} className="px-4 py-2.5 bg-gray-100 text-gray-600 rounded-xl text-sm hover:bg-gray-200 transition-colors"><X className="w-4 h-4" /></button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="offers-reviews-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">العروض والتقييمات</h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        <button onClick={() => setTab("offers")} className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-colors ${tab === "offers" ? "bg-red-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`} data-testid="tab-offers">
          <Tag className="w-4 h-4" />العروض ({offers.length})
        </button>
        <button onClick={() => setTab("reviews")} className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-colors ${tab === "reviews" ? "bg-amber-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`} data-testid="tab-reviews">
          <Star className="w-4 h-4" />التقييمات ({reviews.length})
        </button>
      </div>

      {/* OFFERS TAB */}
      {tab === "offers" && (
        <div className="space-y-4">
          <button onClick={() => { setShowForm(true); setEditItem(null); setForm({}); }} className="flex items-center gap-2 px-5 py-2.5 bg-red-500 text-white rounded-xl text-sm font-bold hover:bg-red-400 transition-colors" data-testid="add-offer-btn">
            <Plus className="w-4 h-4" />إضافة عرض
          </button>
          {showForm && offerForm()}
          {offers.length === 0 ? (
            <div className="text-center py-12 text-gray-400"><Tag className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>لا توجد عروض</p></div>
          ) : (
            offers.map(offer => (
              <div key={offer.id} className={`bg-white rounded-2xl border p-5 ${offer.is_active ? "" : "opacity-50"}`} data-testid={`offer-item-${offer.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-gray-800">{offer.title_ar}</h3>
                      {offer.discount_percent > 0 && <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs font-bold rounded-full">{offer.discount_percent}%</span>}
                      <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${offer.is_active ? "bg-green-100 text-green-600" : "bg-gray-100 text-gray-500"}`}>{offer.is_active ? "مفعّل" : "متوقف"}</span>
                    </div>
                    {offer.description_ar && <p className="text-sm text-gray-500 mb-2">{offer.description_ar}</p>}
                    <div className="flex items-center gap-4 text-xs text-gray-400">
                      {offer.offer_price > 0 && <span className="text-amber-600 font-bold">{Number(offer.offer_price).toLocaleString()} د.ع</span>}
                      {offer.original_price > 0 && <span className="line-through">{Number(offer.original_price).toLocaleString()} د.ع</span>}
                      {offer.end_date && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />ينتهي: {formatDate(offer.end_date)}</span>}
                      {offer.max_clients > 0 && <span>{offer.claimed_count || 0}/{offer.max_clients} حجزوا</span>}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => toggleOfferActive(offer)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-testid={`toggle-offer-${offer.id}`}>
                      {offer.is_active ? <EyeOff className="w-4 h-4 text-gray-400" /> : <Eye className="w-4 h-4 text-green-500" />}
                    </button>
                    <button onClick={() => { setEditItem(offer); setForm(offer); setShowForm(true); }} className="p-2 hover:bg-gray-100 rounded-lg transition-colors"><Edit2 className="w-4 h-4 text-blue-500" /></button>
                    <button onClick={() => deleteOffer(offer.id)} className="p-2 hover:bg-red-50 rounded-lg transition-colors"><Trash2 className="w-4 h-4 text-red-400" /></button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* REVIEWS TAB */}
      {tab === "reviews" && (
        <div className="space-y-4">
          {/* Send Review Link */}
          <div className="bg-amber-50 rounded-2xl border border-amber-200 p-4">
            <h3 className="font-bold text-amber-800 mb-3 text-sm flex items-center gap-2"><Send className="w-4 h-4" />إرسال رابط تقييم للعميل</h3>
            <div className="flex gap-2 flex-wrap">
              <input value={linkName} onChange={e => setLinkName(e.target.value)} placeholder="اسم العميل" className="px-3 py-2 rounded-lg border text-sm focus:border-amber-400 focus:outline-none flex-1 min-w-[150px]" data-testid="review-link-name" />
              <input value={linkPhone} onChange={e => setLinkPhone(e.target.value)} placeholder="رقم الهاتف" type="tel" dir="ltr" className="px-3 py-2 rounded-lg border text-sm focus:border-amber-400 focus:outline-none w-40 text-left" data-testid="review-link-phone" />
              <button onClick={sendReviewLink} disabled={sendingLink || !linkPhone.trim()} className="flex items-center gap-1 px-4 py-2 bg-green-500 text-white rounded-lg text-sm font-bold hover:bg-green-400 disabled:opacity-40 transition-colors" data-testid="send-review-link">
                {sendingLink ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />}أرسل واتساب
              </button>
            </div>
          </div>

          {reviews.length === 0 ? (
            <div className="text-center py-12 text-gray-400"><Star className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>لا توجد تقييمات بعد</p></div>
          ) : (
            reviews.map(review => (
              <div key={review.id} className={`bg-white rounded-2xl border p-5 ${review.approved ? "" : "border-amber-200 bg-amber-50/30"}`} data-testid={`review-item-${review.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-bold text-gray-800">{review.patient_name}</span>
                      <div className="flex gap-0.5">{[...Array(review.rating || 5)].map((_, j) => <Star key={j} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}</div>
                      <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${review.approved ? "bg-green-100 text-green-600" : "bg-amber-100 text-amber-600"}`}>
                        {review.approved ? "معتمد" : "بانتظار الموافقة"}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-1">{review.text}</p>
                    <p className="text-xs text-gray-400">{formatDate(review.created_at)} {review.service && `- ${review.service}`}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => toggleApproval(review)} className={`p-2 rounded-lg transition-colors ${review.approved ? "hover:bg-red-50" : "hover:bg-green-50"}`} data-testid={`approve-review-${review.id}`}>
                      {review.approved ? <EyeOff className="w-4 h-4 text-red-400" /> : <Check className="w-4 h-4 text-green-500" />}
                    </button>
                    <button onClick={() => deleteReview(review.id)} className="p-2 hover:bg-red-50 rounded-lg transition-colors"><Trash2 className="w-4 h-4 text-red-400" /></button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
